import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Box, Link } from "@chakra-ui/react";
import { ArrowSquareOutIcon } from "@phosphor-icons/react";
import { WithAuth } from "@/hoc";
import { ClientsPaginator } from "@/models";
import { phone_format, showToast } from "@/utils";
import { useClient } from "@/hooks";
import { Pagination2 } from "@/components/Pagination2";
import { DynamicTable } from "@/components/DynamicTable";
import { ClientFormDrawer } from "@/layouts";
import { PageHeading, Search, Tooltip, Icon } from "barte-design-system";
const Clients = () => {
  const { getClients } = useClient();
  const [data, setData] = useState({} as ClientsPaginator);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [filterParams, setFilterParams] = useState("name");
  const [pageSize, setPageSize] = useState(8);
  const [isClientDrawerOpen, setIsClientDrawerOpen] = useState(false);

  const searchTypeOptions = [
    { label: "Nome", value: "name" },
    { label: "Documento", value: "document" },
    { label: "Email", value: "email" },
  ];

  async function getClientsData(filter?: string) {
    setLoading(true)

    try {
      const response = await getClients(
        {
          [filterParams]: filter ? filter : "",
        },
        {
          size: pageSize,
          page: currentPage ,
          sort: "name,ASC",
        }
      );
      setData(response.data);
    } catch (error) {
      error?.response?.data?.errors.map((error) => {
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`,
        });
      });
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    getClientsData();
  }, [currentPage, pageSize]);

  useEffect(() => {
    const totalItems = data?.totalElements || 0;
    const newTotalPages = totalItems > 0 ? Math.max(1, Math.ceil(totalItems / pageSize)) : 1;

    if (currentPage + 1 > newTotalPages && newTotalPages > 0) {
      setCurrentPage(newTotalPages - 1);
    }
  }, [pageSize, data?.totalElements]);

  const navigate = useNavigate();

  const columns = [
    {
      key: "name",
      title: "Cliente",
      dataIndex: "name",
    },
    {
      key: "email",
      title: "E-mail",
      dataIndex: "email",
    },
    {
      key: "phone",
      title: "Telefone",
      dataIndex: "phone",
      width: "200px",
      render: (phone) => phone_format(phone),
    },
    {
      key: "buttonOptions",
      title: "",
      dataIndex: "buttonOptions",
      align: "right" as const,
      width: "80px",
      rowAction: (record) => (
        <Tooltip label="Abrir em nova aba" placement="left">
          <Link
            onClick={(event) => event.stopPropagation()}
            /*@ts-ignore*/
            href={`/clientes/${record?.id}`}
            target="_blank"
            display="inline-flex"
            alignItems="center"
            color="var(--content-secondary)"
            transition="color .2s"
            _hover={{ cursor: "pointer", color: "var(--content-primary)" }}
          >
            <Icon icon={ArrowSquareOutIcon} size={20} />
          </Link>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box letterSpacing="tighter">
      <PageHeading
        title="Clientes"
        onPrimaryAction={() => setIsClientDrawerOpen(true)}
        primaryActionLabel="Adicionar cliente"
      />

      <ClientFormDrawer
        isOpen={isClientDrawerOpen}
        onClose={() => setIsClientDrawerOpen(false)}
        onSuccess={() => {
          setIsClientDrawerOpen(false);
          getClientsData();
        }}
      />

      <Flex
        flexDir="column"
        gap="20px"
        mt=".5rem"
      >
        <Flex align="center" gap="2">
          <Search
            placeholder="Buscar clientes"
            onSearch={getClientsData}
            categories={searchTypeOptions}
            selectedCategory={filterParams}
            onCategoryChange={setFilterParams}
            expanded
          />
        </Flex>
        <DynamicTable
          columns={columns}
          data={data?.content}
          rowKey={(record) => record.id.toString()}
          onRowClick={(record) => navigate(`/clientes/${record.id}`)}
          locale={{ emptyText: "Sem dados de clientes" }}
          loading={loading}
          pagination={{
            currentPage: currentPage + 1 ,
            pageSize: pageSize,
            onPageSizeChange: (size) => setPageSize(size),
            totalItems: data?.totalElements,
            totalPages: data?.totalPages,
            onPageChange: (currentPage) => setCurrentPage(currentPage - 1)
          }
          }
        />

      </Flex>
    </Box>
  );
};

export default WithAuth(Clients);
